// src/components/Notix/Notix.jsx
import React, { createContext, useContext, useState } from "react";
import "./Notix.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationCircle, faInfoCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

const NotixContext = createContext();

const iconMap = {
  success: faCheckCircle,
  error: faExclamationCircle,
  info: faInfoCircle,
  warning: faExclamationTriangle,
};

const Toast = ({ toast, onRemove }) => {
  const [paused, setPaused] = React.useState(false);
  const [remaining, setRemaining] = React.useState(toast.duration);
  const [exiting, setExiting] = React.useState(false);
  const timeoutRef = React.useRef();
  const startTimeRef = React.useRef(Date.now());
  const exitTimeoutRef = React.useRef();

  React.useEffect(() => {
    if (!paused && !exiting) {
      startTimeRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        setExiting(true);
        exitTimeoutRef.current = setTimeout(() => {
          onRemove(toast.id);
        }, 350); // Debe coincidir con la duración de la animación de salida
      }, remaining);
    }
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(exitTimeoutRef.current);
    };
    // eslint-disable-next-line
  }, [paused, remaining, exiting]);

  const handleMouseEnter = () => {
    setPaused(true);
    setRemaining((prev) => prev - (Date.now() - startTimeRef.current));
  };

  const handleMouseLeave = () => {
    setPaused(false);
  };

  return (
    <div className={`notix-container ${toast.position}`}>
      <div
        className={`notix-toast ${toast.type} ${toast.theme} ${exiting ? "exit" : "enter"}`}
        style={{
          "--notix-duration": `${remaining}ms`,
          animationPlayState: paused ? "paused" : "running",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span style={{ marginRight: 12, fontSize: 22, verticalAlign: "middle" }}>
          <FontAwesomeIcon icon={iconMap[toast.type] || faInfoCircle} />
        </span>
        <span style={{ verticalAlign: "middle" }}>{toast.message}</span>
        <div
          className="notix-progress"
          style={{
            animationPlayState: paused ? "paused" : "running",
            animationDuration: `${remaining}ms`,
          }}
        ></div>
      </div>
    </div>
  );
};

export const NotixProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = "info", options = {}) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type,
      duration: options.duration || 3000,
      theme: options.theme || "light",
      position: options.position || "top-right",
    };
    setToasts((prev) => [...prev, toast]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const value = {
    success: (msg, opts) => showToast(msg, "success", opts),
    error: (msg, opts) => showToast(msg, "error", opts),
    info: (msg, opts) => showToast(msg, "info", opts),
    warning: (msg, opts) => showToast(msg, "warning", opts),
  };

  return (
    <NotixContext.Provider value={value}>
      {children}
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </NotixContext.Provider>
  );
};

export const useNotix = () => useContext(NotixContext);
